// v3.15-932: UNIFIED stage-email modal. Renders the same shape for all 4
// templated emails (Fee Disclosure / Application Link / ACH / Appointment
// Link). Rep can edit:
//   - To
//   - CC
//   - Subject (action-specific default)
//   - Top message (the lead-in only)
//
// Everything below the first divider — auto content (link/disclosure),
// contact info (phone + ext), sign-off, credit-report blurb, websites — is
// rendered server-side via /api/email/stage-email-preview and shown in a
// read-only preview pane below the editor. The send endpoint uses the SAME
// builder so what the rep sees is byte-for-byte what gets shipped.
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import TemplateSelector from './TemplateSelector';
import { TemplateLoadedBanner, confirmReplaceDraft } from './templateSelectorHelpers';
import SaveAsTemplateDialog from './SaveAsTemplateDialog';
import { useAuth } from '../contexts/AuthContext';

// v3.15-930: Appointment Link CC defaults to the sales manager + the rep.
// v3.15-1086: removed hardcoded `APPOINTMENT_LINK_DEFAULT_CC` per universal
// email policy ("no hidden recipients"). CC now seeds from the assigned
// deal-team only — see useEffect below that calls
// /api/clients/{id}/default-recipients?context=external. Operator retains
// full edit control.

// Action metadata — title shown in the modal header + default subject hints.
// v3.15-934: subjects/lead-ins refreshed to match the unified high-conversion
// templates. Defaults are mirrored on the backend; the textarea pre-fills from
// here so the rep can edit *before* the first preview round-trip lands.
export const STAGE_EMAIL_TEMPLATES = {
  fee_disclosure:    { title: 'Send Fee Disclosure Email',     defaultSubject: 'Important Information — Please Review' },
  application_link:  { title: 'Send Credit Application Link',  defaultSubject: 'Complete Your Application' },
  disclosure_ach:    { title: 'Send ACH Authorization Link',   defaultSubject: 'Secure Authorization Required' },
  appointment_link:  { title: 'Send Appointment Link',         defaultSubject: 'Let’s Review Your Loan Documents' },
};

// Default short rep lead-ins shown in the editor when the modal opens. Reps
// can clear or rewrite these — server uses its own defaults if blank.
// v3.15-934: high-conversion, fully standardized defaults. The locked URLs
// ({{application_link}}, {{ach_link}}, {{booking_link}}) are substituted by
// the backend on preview/send so what the rep sees here is what gets shipped.
const DEFAULT_LEAD_IN = {
  application_link:
    'Hello {{client_name}},\n\n' +
    'To continue moving forward with your file, please complete your application using the secure link below:\n\n' +
    '{{application_link}}\n\n' +
    'This is a soft credit pull and will not affect your credit score.\n\n' +
    'Once submitted, we’ll review everything and follow up with your available options.\n\n' +
    'If you have any questions, feel free to reply here or call us directly.',
  disclosure_ach:
    'Hello {{client_name}},\n\n' +
    'To continue moving forward with your file, please complete the secure authorization form below:\n\n' +
    '{{ach_link}}\n\n' +
    'This allows us to proceed with the next step in your process.\n\n' +
    'No funds will be drafted from your account until it is 100% confirmed that the loan funds have been successfully deposited.\n\n' +
    'Please complete this at your earliest convenience and let me know once it’s done.',
  appointment_link:
    'Hello {{client_name}},\n\n' +
    'We want to make sure you’ve received your loan documents and have an opportunity to go through everything together.\n\n' +
    'Please use the link below to schedule a time that works best for you:\n\n' +
    '{{booking_link}}\n\n' +
    'During this call, we’ll review your documents, answer any questions, and help make sure everything is completed correctly so there are no unnecessary delays in processing.',
  fee_disclosure:
    'Hello {{client_name}},\n\n' +
    'Please take a moment to review the important information below regarding your application and the services provided through EasyQuickFund.com.\n\n' +
    'This is provided to ensure full transparency as we move forward.',
};

export default function EditableStageEmailModal({ open, onOpenChange, action, client, currentUserEmail, onSent }) {
  const { isAdmin } = useAuth();
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const meta = STAGE_EMAIL_TEMPLATES[action];

  const [to, setTo]            = useState('');
  const [cc, setCc]            = useState('');
  const [subject, setSubject]  = useState('');
  const [topMessage, setTopMsg] = useState('');
  // v3.15-1088 — universal template selector state.
  const [loadedTemplate, setLoadedTemplate] = useState(null);
  const [baselineDraft, setBaselineDraft] = useState({ subject: '', body: '' });
  const [sending, setSending]   = useState(false);

  // Live preview pulled from the server. Rebuilt as the rep types (debounced).
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody,    setPreviewBody]    = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError,   setPreviewError]   = useState(null);

  // Pre-fill on open / when action changes.
  useEffect(() => {
    if (!open || !meta) return;
    setTo(client?.email || '');
    setCc('');  // overridden by /default-recipients fetch below (v3.15-1086)
    setSubject(meta.defaultSubject || '');
    setTopMsg(DEFAULT_LEAD_IN[action] || 'Hello,');
    setPreviewBody('');
    setPreviewSubject('');
    setPreviewError(null);

    // v3.15-1086: seed CC with the assigned deal-team (Director, Manager,
    // Sales Rep, Concierge, Application Manager, VO Officer) — assigned-to-
    // this-deal only, never company-wide. No more hardcoded permanent CCs.
    if (client?.id) {
      api.get(`/clients/${client.id}/default-recipients`, { params: { context: 'external' } })
        .then(({ data }) => {
          const team = (data?.cc || []).filter(Boolean);
          // Include the current user's email as a courtesy so they get a
          // copy of the email they just sent — this matches the prior UX.
          const merged = [...team];
          if (currentUserEmail && !merged.some(e => e.toLowerCase() === currentUserEmail.toLowerCase())) {
            merged.push(currentUserEmail);
          }
          if (merged.length) setCc(merged.join(', '));
          else if (currentUserEmail) setCc(currentUserEmail);
        })
        .catch(() => {
          // Fall back to caller-only CC if the resolver errors.
          if (currentUserEmail) setCc(currentUserEmail);
        });
    }
  }, [open, action, client?.id, client?.email, currentUserEmail, meta]);

  // Debounced live preview. Re-fetches whenever the rep edits the top
  // message or subject so what they see is the EXACT body that will ship.
  const previewKey = useMemo(
    () => `${open ? 1 : 0}|${action}|${client?.id || ''}|${subject}|${topMessage}`,
    [open, action, client?.id, subject, topMessage]
  );
  useEffect(() => {
    if (!open || !action) return;
    let alive = true;
    setPreviewLoading(true);
    setPreviewError(null);
    const t = setTimeout(() => {
      api.post('/email/stage-email-preview', {
        action,
        client_id: client?.id || '',
        rep_message: topMessage,
        subject,
      })
        .then(({ data }) => {
          if (!alive) return;
          if (!data?.ok) {
            setPreviewError(data?.error === 'disclosure_not_loaded'
              ? 'Disclosure text failed to load. Send is blocked.'
              : (data?.error || 'Preview failed.'));
            setPreviewBody('');
            setPreviewSubject('');
            return;
          }
          setPreviewBody(String(data.body || ''));
          setPreviewSubject(String(data.subject || ''));
          if (data?.warning === 'no_booking_slug' && action === 'appointment_link') {
            setPreviewError('No booking slug configured. Set one in Settings → Availability before sending.');
          }
        })
        .catch((e) => {
          if (!alive) return;
          setPreviewError(e?.response?.data?.detail || 'Preview failed to load.');
        })
        .finally(() => { if (alive) setPreviewLoading(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [previewKey]);

  if (!meta) return null;

  const send = async () => {
    if (!to.trim()) { toast.error('"To" address is required'); return; }
    if (previewError) { toast.error(previewError); return; }
    setSending(true);
    try {
      const ccArr = cc.split(',').map((x) => x.trim()).filter(Boolean);
      // v3.15-932: only `subject` and the top message (`body`) are sent. The
      // backend rebuilds the entire envelope using its canonical builder.
      const payload = {
        action,
        client_id: client?.id,
        to: to.trim(),
        cc: ccArr,
        subject: subject.trim(),
        body: topMessage,
        // v3.15-1088 — stamp template usage (extras tolerated server-side).
        template_id:       loadedTemplate?.id || null,
        template_name:     loadedTemplate?.name || null,
        template_key:      loadedTemplate?.key || null,
        template_category: loadedTemplate?.category || null,
      };
      const { data } = await api.post('/email/send-stage-email', payload);
      toast.success(`Email sent from ${data.from}${data.cc?.length ? ' (CC: ' + data.cc.join(', ') + ')' : ''}`);
      onSent && onSent({ action, stage_key: data.stage_key });
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to send email';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid={`stage-email-modal-${action}`}>
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
            From: <span className="font-mono">reply@easyquickfund.com</span> (fixed)
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">To</label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@example.com" data-testid="stage-email-to" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">CC <span className="text-slate-400 font-normal">(comma-separated)</span></label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="manager@yourcompany.com" data-testid="stage-email-cc" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Subject</label>
            {/* v3.15-1088 — template selector. Default action subject remains
                pre-filled by the action metadata above; selector lets the
                operator switch to any registry template before sending. */}
            <div className="flex items-center justify-between mb-1">
              <TemplateSelector
                clientId={client?.id}
                onSelect={({ template, rendered_subject, rendered_body }) => {
                  if (!confirmReplaceDraft({ subject, body: topMessage }, baselineDraft)) return;
                  setSubject(rendered_subject || meta.defaultSubject || '');
                  setTopMsg(rendered_body || '');
                  setBaselineDraft({ subject: rendered_subject || '', body: rendered_body || '' });
                  setLoadedTemplate(template);
                }}
                testId="stage-email-template"
              />
              {loadedTemplate && (
                <button
                  type="button"
                  onClick={() => setLoadedTemplate(null)}
                  className="text-[11px] text-slate-400 hover:text-slate-600 underline"
                  data-testid="stage-email-template-clear"
                >
                  Clear template
                </button>
              )}
            </div>
            <TemplateLoadedBanner template={loadedTemplate} testId="stage-email-template-banner" />
            {isAdmin?.() && (
              <div className="flex justify-end mb-1">
                <button
                  type="button"
                  onClick={() => setSaveTplOpen(true)}
                  className="text-[11px] text-blue-600 hover:text-blue-800 underline"
                  data-testid="stage-email-save-as-template"
                >
                  Save As Template…
                </button>
              </div>
            )}
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="stage-email-subject" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Top message (rep editable — only this section)</label>
            <Textarea
              value={topMessage}
              onChange={(e) => setTopMsg(e.target.value)}
              rows={4}
              className="font-sans"
              data-testid="stage-email-body"
            />
            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
              <Lock className="h-2.5 w-2.5" />
              Locked sections (auto-generated): the application/ACH/booking link, fee disclosure text, contact information, and the sign-off footer.
            </p>
          </div>

          {/* Unified read-only preview — what the recipient will receive */}
          <div className="space-y-1.5" data-testid="stage-email-preview-section">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                Full Email Preview (Read Only)
              </label>
              {previewLoading && (
                <span className="text-[11px] text-slate-500 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Building preview…
                </span>
              )}
            </div>
            {previewError ? (
              <div
                className="flex items-start gap-2 rounded border border-red-300 bg-red-50 px-3 py-2.5 text-xs text-red-800"
                data-testid="stage-email-preview-error"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold mb-0.5">Preview unavailable</div>
                  <div>{previewError}</div>
                  <div className="mt-1 text-red-700">Send is blocked until this resolves.</div>
                </div>
              </div>
            ) : (
              <div
                className="rounded border border-slate-300 bg-slate-50 text-[12px] leading-relaxed font-mono text-slate-800 max-h-96 overflow-y-auto"
                data-testid="stage-email-preview"
              >
                {previewSubject && (
                  <div className="px-3 py-1.5 border-b border-slate-200 bg-white text-slate-700 flex items-center gap-2" data-testid="stage-email-preview-subject">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Subject:</span>
                    <span className="text-[12px]">{previewSubject}</span>
                  </div>
                )}
                <div className="px-3 py-2.5 whitespace-pre-wrap">{previewBody || (previewLoading ? '' : '(empty)')}</div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={sending} data-testid="stage-email-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={send}
            disabled={sending || !!previewError || previewLoading || !previewBody}
            data-testid="stage-email-send"
          >
            {sending ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Sending…</> : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
      <SaveAsTemplateDialog
        open={saveTplOpen}
        onOpenChange={setSaveTplOpen}
        defaultSubject={subject}
        defaultBody={topMessage}
      />
    </Dialog>
  );
}
